// src/components/auth/EmailVerificationHandler.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../lib/firebase';
import { applyActionCode, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

export const EmailVerificationHandler: React.FC = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('Verifiziere Ihre E-Mail...');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [codeApplied, setCodeApplied] = useState<boolean>(false);

  const handleEmailVerification = async (code: string, user: any = null) => {
    try {
      await applyActionCode(auth, code);
      
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        
        if (!userData) {
          throw new Error('Benutzerdaten nicht gefunden');
        }

        if (userData.role !== 'manager') {
          const settingsDoc = await getDoc(doc(db, 'settings', 'userPermissions'));
          const settings = settingsDoc.data();
          
          const updateData: any = {
            emailVerified: true,
            updatedAt: new Date(),
            approved: settings?.autoApproveUsers === true
          };

          if (settings?.autoApproveUsers) {
            updateData.approvedAt = new Date();
          }

          await updateDoc(userRef, updateData);
        } else {
          await updateDoc(userRef, {
            emailVerified: true,
            updatedAt: new Date(),
            approved: false // Managers always need manual approval
          });
        }
        
        await signOut(auth);
      }
      
      setMessage('Ihre E-Mail wurde erfolgreich verifiziert. Sie können sich jetzt anmelden.');
      setCodeApplied(true);
      setTimeout(() => navigate('/'), 3000);
      
    } catch (err) {
      console.error('Fehler bei der E-Mail-Verifizierung:', err);
      setError('Es gab ein Problem bei der Verifizierung Ihrer E-Mail. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('oobCode');
    setOobCode(code);

    if (!code) {
      setError('Ungültiger Verifizierungslink.');
      setLoading(false);
      return;
    }

    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await handleEmailVerification(code, user);
      } else {
        await handleEmailVerification(code);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="p-6 bg-white dark:bg-gray-800 rounded shadow-md w-full max-w-md mx-auto">
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">E-Mail-Verifizierung</h2>
        {loading ? (
          <p className="text-gray-700 dark:text-gray-300">Verifiziere Ihre E-Mail...</p>
        ) : error ? (
          <p className="text-red-600 dark:text-red-400">{error}</p>
        ) : codeApplied ? (
          <p className="text-green-600 dark:text-green-400">{message}</p>
        ) : (
          <p className="text-gray-700 dark:text-gray-300">{message}</p>
        )}
        {!codeApplied && !error && !loading && oobCode && (
          <div className="mt-4">
            <button
              onClick={() => navigate(`/login?oobCode=${encodeURIComponent(oobCode)}`)}
              className="mt-2 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Zur Anmeldung
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
